import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  Badge,
  Spinner
} from "reactstrap";
import classnames from "classnames";
import SimpleBar from "simplebar-react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { get, put } from "../../../helpers/api_helper";
import { NOTIFICATIONS_URL } from "../../../helpers/url_helper";
import { getUserRole } from "../../../helpers/permissionHelper";
import cookieHelper from "../../../helpers/getCookieData";

const NotificationDropdown = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const isMountedRef = useRef(true);

  const [menu, setMenu] = useState(false);
  const [activeTab, setActiveTab] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({
    total_unread: 0,
    urgent_count: 0,
    bookings_count: 0,
    followups_count: 0,
    payments_count: 0
  });

  const userRole = getUserRole();
  const isPatient = userRole === "patient";
  const isAdmin = userRole === "admin" || userRole === "superadmin";

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch unread count badge
  const fetchUnreadCount = useCallback(async () => {
    const token = cookieHelper.getCookie("access_token");
    if (!token) return;
    try {
      const res = await get(`${NOTIFICATIONS_URL}/unread-count`);
      if (isMountedRef.current && res && (res.success || res.body)) {
        setUnreadCounts(res.body || res.data || { total_unread: 0, urgent_count: 0 });
      }
    } catch (err) {
      // ignore network hiccup
    }
  }, []);

  // Fetch notification list
  const fetchNotifications = useCallback(async (tab = activeTab) => {
    if (!isMountedRef.current) return;
    const token = cookieHelper.getCookie("access_token");
    if (!token) return;
    setLoading(true);
    try {
      let queryUrl = `${NOTIFICATIONS_URL}?limit=15`;
      if (tab === "UNREAD") {
        queryUrl += `&is_read=false`;
      } else if (tab !== "ALL") {
        queryUrl += `&type=${tab}`;
      }

      const res = await get(queryUrl);
      if (isMountedRef.current && res && res.success) {
        const items = res.body?.items || res.data?.items || [];
        setNotifications(items);
      }
    } catch (err) {
      console.error("Error loading notifications:", err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [activeTab]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(() => {
      if (isMountedRef.current) {
        fetchUnreadCount();
      }
    }, 30000); // 30s polling
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (menu && isMountedRef.current) {
      fetchNotifications(activeTab);
    }
  }, [menu, activeTab, fetchNotifications]);

  // Mark single as read
  const handleMarkAsRead = async (e, id) => {
    e.stopPropagation();
    try {
      await put(`${NOTIFICATIONS_URL}/${id}/read`);
      if (isMountedRef.current) {
        setNotifications(prev =>
          prev.map(n => (n.id === id ? { ...n, read_at: new Date() } : n))
        );
        fetchUnreadCount();
      }
    } catch (err) {
      console.error("Error marking read:", err);
    }
  };

  // Dismiss single notification
  const handleDismiss = async (e, id) => {
    e.stopPropagation();
    try {
      await put(`${NOTIFICATIONS_URL}/${id}/dismiss`);
      if (isMountedRef.current) {
        setNotifications(prev => prev.filter(n => n.id !== id));
        fetchUnreadCount();
      }
    } catch (err) {
      console.error("Error dismissing notification:", err);
    }
  };

  // Mark all read
  const handleMarkAllRead = async () => {
    try {
      await put(`${NOTIFICATIONS_URL}/mark-all-read`);
      if (isMountedRef.current) {
        setNotifications(prev => prev.map(n => ({ ...n, read_at: new Date() })));
        fetchUnreadCount();
      }
    } catch (err) {
      console.error("Error marking all read:", err);
    }
  };

  // Handle clicking notification item
  const handleItemClick = (item) => {
    if (!item.read_at) {
      put(`${NOTIFICATIONS_URL}/${item.id}/read`).catch(() => { });
      fetchUnreadCount();
    }
    setMenu(false);
    if (isPatient) {
      history.push("/portal");
    } else if (item.action_url) {
      history.push(item.action_url);
    }
  };

  // Color scheme & icons by category
  const getTypeBadge = (type) => {
    switch (type) {
      case "BOOKING":
        return {
          icon: "bx bx-calendar-event",
          bgColor: "#eff6ff",
          textColor: "#2563eb",
          label: t("Booking") || "Booking",
        };
      case "FOLLOW_UP":
        return {
          icon: "bx bx-phone-call",
          bgColor: "#fef3c7",
          textColor: "#b45309",
          label: t("Follow-up") || "Follow-up",
        };
      case "PAYMENT":
        return {
          icon: "bx bx-credit-card",
          bgColor: "#ecfdf5",
          textColor: "#059669",
          label: t("Payment") || "Payment",
        };
      case "LAB_REPORT":
        return {
          icon: "bx bx-test-tube",
          bgColor: "#f3e8ff",
          textColor: "#7e22ce",
          label: t("Lab Work") || "Lab Work",
        };
      case "TASK":
        return {
          icon: "bx bx-task",
          bgColor: "#f1f5f9",
          textColor: "#475569",
          label: t("Task") || "Task",
        };
      default:
        return {
          icon: "bx bx-bell",
          bgColor: "#f8fafc",
          textColor: "#64748b",
          label: t("Notice") || "Notice",
        };
    }
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="dropdown d-inline-block"
        tag="li"
      >
        <DropdownToggle
          className="btn header-item noti-icon position-relative"
          tag="button"
          id="page-header-notifications-dropdown"
        >
          <i className={classnames("bx bx-bell", { "bx-tada": unreadCounts.total_unread > 0 })} />
          {unreadCounts.total_unread > 0 && (
            <span
              className="badge rounded-pill position-absolute font-size-10"
              style={{
                top: "10px",
                right: "6px",
                backgroundColor: "#ef4444",
                color: "#ffffff",
                boxShadow: "0 0 0 2px #ffffff"
              }}
            >
              {unreadCounts.total_unread > 99 ? "99+" : unreadCounts.total_unread}
            </span>
          )}
        </DropdownToggle>

        <DropdownMenu
          className="dropdown-menu dropdown-menu-lg p-0 dropdown-menu-end shadow-lg border-0 rounded-4"
          style={{
            width: "420px",
            maxWidth: "92vw",
            zIndex: 1050,
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.06)",
            border: "1px solid rgba(0,0,0,0.08)"
          }}
        >
          {/* Top Header */}
          <div className="p-3 bg-white border-bottom">
            <div className="d-flex align-items-center justify-content-between mb-2.5">
              <div className="d-flex align-items-center gap-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center shadow-2xs flex-shrink-0"
                  style={{ width: "34px", height: "34px", backgroundColor: "#fef3c7", color: "#d97706" }}
                >
                  <i className="bx bx-bell font-size-17" />
                </div>
                <div>
                  <h6 className="m-0 font-size-14 font-weight-bold text-dark">
                    {isPatient ? t("Notifications & Updates") : t("Tasks & Notifications")}
                  </h6>
                </div>
                {unreadCounts.total_unread > 0 && (
                  <span
                    className="badge rounded-pill font-size-11 px-2 py-0.5 font-weight-semibold shadow-2xs"
                    style={{ backgroundColor: "#1e293b", color: "#F8CA15" }}
                  >
                    {unreadCounts.total_unread} {t("new")}
                  </span>
                )}
              </div>

              {unreadCounts.total_unread > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="btn btn-sm btn-link p-0 text-muted font-size-11 font-weight-semibold text-decoration-none d-inline-flex align-items-center gap-1"
                  title={t("Mark all as read")}
                >
                  <i className="bx bx-check-double text-success font-size-14" />
                  <span>{t("Mark all read")}</span>
                </button>
              )}
            </div>

            {/* Segmented Filter Pills */}
            <div className="d-flex align-items-center gap-1.5 overflow-auto pt-1 pb-0.5" style={{ scrollbarWidth: "none" }}>
              <button
                type="button"
                className={`btn btn-sm rounded-pill font-size-11 px-2.5 py-1 font-weight-semibold transition-all border ${
                  activeTab === "ALL"
                    ? "btn-dark text-white shadow-2xs"
                    : "btn-light text-secondary bg-light border-light"
                }`}
                onClick={() => setActiveTab("ALL")}
              >
                {t("All")}
              </button>

              <button
                type="button"
                className={`btn btn-sm rounded-pill font-size-11 px-2.5 py-1 font-weight-semibold transition-all border ${
                  activeTab === "UNREAD"
                    ? "btn-dark text-white shadow-2xs"
                    : "btn-light text-secondary bg-light border-light"
                }`}
                onClick={() => setActiveTab("UNREAD")}
              >
                {t("Unread")}
                {unreadCounts.total_unread > 0 && (
                  <span className="badge bg-danger text-white rounded-pill ms-1 px-1.5 py-0.5 font-size-10">
                    {unreadCounts.total_unread}
                  </span>
                )}
              </button>

              {!isPatient && (
                <>
                  <button
                    type="button"
                    className={`btn btn-sm rounded-pill font-size-11 px-2.5 py-1 font-weight-semibold transition-all border ${
                      activeTab === "BOOKING"
                        ? "btn-dark text-white shadow-2xs"
                        : "btn-light text-secondary bg-light border-light"
                    }`}
                    onClick={() => setActiveTab("BOOKING")}
                  >
                    {t("Bookings")}
                  </button>

                  <button
                    type="button"
                    className={`btn btn-sm rounded-pill font-size-11 px-2.5 py-1 font-weight-semibold transition-all border ${
                      activeTab === "FOLLOW_UP"
                        ? "btn-dark text-white shadow-2xs"
                        : "btn-light text-secondary bg-light border-light"
                    }`}
                    onClick={() => setActiveTab("FOLLOW_UP")}
                  >
                    {t("Follow-ups")}
                  </button>

                  {isAdmin && (
                    <button
                      type="button"
                      className={`btn btn-sm rounded-pill font-size-11 px-2.5 py-1 font-weight-semibold transition-all border ${
                        activeTab === "PAYMENT"
                          ? "btn-dark text-white shadow-2xs"
                          : "btn-light text-secondary bg-light border-light"
                      }`}
                      onClick={() => setActiveTab("PAYMENT")}
                    >
                      {t("Payments")}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* List Area */}
          <SimpleBar style={{ maxHeight: "360px" }}>
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" size="sm" />
                <div className="text-muted font-size-12 mt-2">{t("Loading notifications...")}</div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-5 px-3 text-muted">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                  style={{ width: "48px", height: "48px", backgroundColor: "#f1f5f9" }}
                >
                  <i className="bx bx-bell-off font-size-24 text-muted" />
                </div>
                <div className="font-size-13 font-weight-bold text-dark">{t("No notifications")}</div>
                <div className="font-size-11 text-muted mt-0.5">{t("You are completely caught up.")}</div>
              </div>
            ) : (
              notifications.map((item) => {
                const meta = getTypeBadge(item.type);
                const isUnread = !item.read_at;
                const isUrgent = item.priority === "URGENT" || item.priority === "HIGH";

                return (
                  <div
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    className={`px-3 py-2.5 border-bottom text-reset d-block cursor-pointer position-relative transition-all ${
                      isUnread ? "bg-light bg-opacity-75" : "bg-white hover-bg-light"
                    }`}
                    style={{
                      transition: "background 0.15s ease",
                    }}
                  >
                    {/* Left Inset Indicator Accent */}
                    {isUnread && (
                      <div
                        className="position-absolute top-0 start-0 bottom-0"
                        style={{
                          width: "4px",
                          backgroundColor: isUrgent ? "#ef4444" : "#F8CA15",
                        }}
                      />
                    )}

                    <div className="d-flex align-items-start gap-2.5">
                      {/* Icon Avatar */}
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 shadow-2xs mt-0.5"
                        style={{
                          width: "36px",
                          height: "36px",
                          backgroundColor: meta.bgColor,
                          color: meta.textColor,
                        }}
                      >
                        <i className={`${meta.icon} font-size-17`} />
                      </div>

                      {/* Content Area */}
                      <div className="flex-grow-1 min-w-0" style={{ overflow: "hidden" }}>
                        <div className="d-flex align-items-center justify-content-between mb-1 gap-2">
                          <span
                            className="font-size-10 font-weight-bold px-2 py-0.5 rounded-pill"
                            style={{ backgroundColor: meta.bgColor, color: meta.textColor }}
                          >
                            {meta.label}
                          </span>
                          <span className="font-size-10 text-muted d-flex align-items-center gap-1 flex-shrink-0">
                            <i className="bx bx-time font-size-11 text-muted" />
                            {moment(item.created_at).fromNow()}
                          </span>
                        </div>

                        <div
                          className={`font-size-13 mb-1 line-clamp-1 ${
                            isUnread ? "font-weight-bold text-dark" : "font-weight-semibold text-secondary"
                          }`}
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: "1.35"
                          }}
                        >
                          {item.title}
                        </div>

                        {item.message && (
                          <div
                            className="font-size-11 text-muted mb-1.5"
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              lineHeight: "1.4"
                            }}
                          >
                            {item.message}
                          </div>
                        )}

                        <div className="d-flex align-items-center justify-content-between pt-0.5">
                          {item.action_url ? (
                            <span
                              className="font-size-11 font-weight-bold d-inline-flex align-items-center gap-1 text-primary text-decoration-none"
                            >
                              <span>{t("View Details")}</span>
                              <i className="bx bx-right-arrow-alt font-size-13" />
                            </span>
                          ) : <div />}

                          {/* Quick Actions (Inline) */}
                          <div className="d-flex align-items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            {isUnread && (
                              <button
                                type="button"
                                onClick={(e) => handleMarkAsRead(e, item.id)}
                                className="btn btn-xs btn-light rounded-circle p-0 d-flex align-items-center justify-content-center border shadow-2xs"
                                title={t("Mark as read")}
                                style={{ width: "22px", height: "22px", color: "#10b981", backgroundColor: "#fff" }}
                              >
                                <i className="bx bx-check font-size-13" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(e) => handleDismiss(e, item.id)}
                              className="btn btn-xs btn-light rounded-circle p-0 d-flex align-items-center justify-content-center border shadow-2xs"
                              title={t("Dismiss")}
                              style={{ width: "22px", height: "22px", color: "#94a3b8", backgroundColor: "#fff" }}
                            >
                              <i className="bx bx-x font-size-13" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </SimpleBar>

          {/* Footer */}
          <div className="py-2.5 px-3 border-top text-center bg-light">
            <Link
              className="btn btn-sm btn-link font-size-12 font-weight-bold text-dark d-inline-flex align-items-center gap-1.5 text-decoration-none"
              to="/tasks-notifications"
              onClick={() => setMenu(false)}
            >
              <i className="bx bx-list-check font-size-16 text-warning" />
              <span>{t("View All Tasks & Notifications")}</span>
              <i className="bx bx-right-arrow-alt font-size-14 text-muted" />
            </Link>
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default NotificationDropdown;